import React, { useState, useEffect, useCallback } from 'react';
import { CheckSquare, Pencil, Trash2 } from 'lucide-react';
import { API_BASE } from '../api';
import SportsbookLogo, { SPORTSBOOK_NAMES } from './SportsbookLogo';

function bookDisplayName(key) {
  if (!key) return '—';
  return SPORTSBOOK_NAMES[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const TYPE_LABELS = { ev: '+EV', arb: 'Arb', promo: 'Promo', manual: 'Manual', parlay: 'Parlay' };
const RESULT_LABELS = { win: 'Won', loss: 'Lost', push: 'Push', pending: 'Open', expired: 'Expired', cancelled: 'Cancelled' };
const PROMO_TYPE_LABELS = { free_bet: 'Free Bet', no_sweat: 'No Sweat', profit_boost: 'Profit Boost', deposit_bonus: 'Deposit Bonus', bet_and_get: 'Bet & Get' };

function getMarketLabel(market, notes, side) {
  const point = notes?.point;
  if (market === 'h2h') return 'Moneyline';
  if (market === 'spreads') return point != null ? `Spread ${Math.abs(point)}` : 'Spread';
  if (market === 'totals') return point != null ? `Total ${point}` : 'Total';
  if (market === 'player_props' || market === 'player_prop') return 'Player Prop';
  if (side && /\s(over|under)\s[\d.]/i.test(side) && !/^(over|under)\s/i.test(side.trim())) {
    return 'Player Prop';
  }
  return null;
}

function formatOdds(odds) {
  if (odds == null) return '—';
  return odds > 0 ? `+${odds}` : `${odds}`;
}

function parseUtc(iso) {
  if (!iso) return null;
  const normalized = /Z|[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + 'Z';
  return new Date(normalized);
}

function formatDateTime(iso) {
  const d = parseUtc(iso);
  if (!d) return null;
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date} ${time}`;
}

function formatPL(pl) {
  if (pl == null) return '—';
  const sign = pl >= 0 ? '+' : '-';
  return `${sign}$${Math.abs(pl).toFixed(2)}`;
}

const PROP_LABELS = {
  batter_hits: '(Hits)', batter_rbis: '(RBIs)', batter_home_runs: '(HRs)',
  batter_runs_scored: '(Runs)', batter_strikeouts: '(Strikeouts)', batter_walks: '(Walks)',
  batter_singles: '(Singles)', batter_doubles: '(Doubles)', batter_triples: '(Triples)',
  batter_total_bases: '(Total Bases)', batter_hits_runs_rbis: '(H+R+RBI)',
  pitcher_strikeouts: '(Strikeouts)', pitcher_hits_allowed: '(Hits Allowed)',
  pitcher_walks: '(Walks)', pitcher_earned_runs: '(Earned Runs)', pitcher_outs: '(Outs)',
  player_pass_tds: '(Pass TDs)', player_pass_yds: '(Pass Yds)',
  player_pass_attempts: '(Pass Att)', player_pass_completions: '(Completions)',
  player_rush_yds: '(Rush Yds)', player_rush_attempts: '(Rush Att)',
  player_reception_yds: '(Rec Yds)', player_receptions: '(Receptions)',
  player_points: '(Points)', player_assists: '(Assists)', player_rebounds: '(Rebounds)',
  player_threes: '(3-Pointers)', player_blocks: '(Blocks)', player_steals: '(Steals)',
  player_turnovers: '(Turnovers)', player_shots: '(Shots)', player_goals: '(Goals)',
  player_kicking_points: '(Kicking Pts)', player_field_goals: '(FGs)',
};

function getPropLabel(market, notes) {
  const raw = notes?.prop_type;
  if (raw) {
    return PROP_LABELS[raw] || (raw.startsWith('(') ? raw : `(${raw})`);
  }
  return market ? (PROP_LABELS[market] || null) : null;
}

function withPropLabel(side, propLabel) {
  if (!side || !propLabel) return side || '';
  if (/\([^)]+\)\s*$/.test(side)) return side;
  return `${side} ${propLabel}`;
}

function shortName(side, propLabel) {
  if (!side) return '';
  if (/^(over|under)\s/i.test(side)) return side.split(' ')[0];
  const ouMatch = side.match(/\b(over|under)\s+([\d.]+)(\s*\([^)]+\))?/i);
  if (ouMatch) {
    const dir = ouMatch[1][0].toUpperCase() + ouMatch[1].slice(1).toLowerCase();
    const suffix = ouMatch[3] ? (' ' + ouMatch[3].trim()) : (propLabel ? (' ' + propLabel) : '');
    return `${dir} ${ouMatch[2]}${suffix}`;
  }
  return side.split(' ').pop();
}

function safeParseNotes(notes) {
  if (!notes) return null;
  try { return JSON.parse(notes); } catch { return null; }
}

function describeBet(bet) {
  if (bet.sideB != null) return null;
  if (bet.betType === 'parlay') return null;
  const side = bet.side || '';
  if (!side) return null;

  const market = bet.market || 'h2h';
  const notes = safeParseNotes(bet.notes);
  const notesPoint = notes?.point;

  if (market === 'h2h') {
    return side === 'Draw' ? 'Draw' : `${side} ML`;
  }
  if (market === 'spreads') {
    if (/[+-]\d/.test(side)) return side;
    if (notesPoint != null) {
      const sign = notesPoint > 0 ? '+' : '';
      return `${side} ${sign}${notesPoint}`;
    }
    return `${side} (spread)`;
  }
  if (market === 'totals') {
    if (/\d/.test(side)) return side;
    if (notesPoint != null) return `${side} ${notesPoint}`;
    return side;
  }
  if (market === 'player_props') {
    const playerName = notes?.player_name;
    const propLabel = getPropLabel(market, notes);
    let result = side;
    if (notesPoint != null && !/\d/.test(side)) {
      result = `${side} ${notesPoint}`;
    }
    if (propLabel && !result.includes(propLabel.replace(/[()]/g, ''))) {
      result = `${result} ${propLabel}`;
    }
    if (playerName && !result.toLowerCase().includes(playerName.toLowerCase().split(' ')[0])) {
      result = `${playerName} · ${result}`;
    }
    return result;
  }
  return side;
}

function oddsToDecimal(american) {
  if (!american) return 2;
  if (american > 0) return american / 100 + 1;
  return 100 / Math.abs(american) + 1;
}

function SettleForm({ bet, onSettle, onCancel }) {
  const [result, setResult] = useState('win');
  const [payout, setPayout] = useState('');

  function handleSubmit(e) {
    e.preventDefault();
    const profitLoss = result === 'win'
      ? parseFloat(payout) - (bet.amount || 0)
      : result === 'loss' ? -(bet.amount || 0) : 0;
    onSettle(bet.id, result, result === 'win' ? profitLoss : undefined);
  }

  return (
    <form className="bh-settle-form" onSubmit={handleSubmit} onClick={e => e.stopPropagation()}>
      <select value={result} onChange={e => setResult(e.target.value)} className="bh-settle-select">
        <option value="win">Win</option>
        <option value="loss">Loss</option>
        <option value="push">Push</option>
      </select>
      {result === 'win' && (
        <input
          type="number"
          className="bh-settle-input"
          placeholder="Total payout ($)"
          value={payout}
          onChange={e => setPayout(e.target.value)}
          step="0.01"
          min="0"
          required
        />
      )}
      <button type="submit" className="bh-settle-submit">Save</button>
      <button type="button" className="bh-settle-cancel" onClick={onCancel}>✕</button>
    </form>
  );
}

function ArbSettleForm({ bet, notes, onSettle, onCancel }) {
  const isThreeWay = notes?.is_three_way;
  const amtA = bet.amount || 0;
  const amtB = bet.amountB || 0;
  const amtC = notes?.amount_c || 0;
  const decA = oddsToDecimal(bet.odds);
  const decB = oddsToDecimal(bet.oddsB);
  const decC = notes?.odds_c ? oddsToDecimal(notes.odds_c) : 1;
  const isFreeBetA = notes?.promo_type === 'free_bet' || (notes?.promo_type === 'bet_and_get' && notes?.stage !== 'qualifying');
  const isFreeBetB = notes?.is_free_money_b === true;

  const profitA = amtA * (decA - 1) - (isFreeBetB ? 0 : amtB) - amtC;
  const profitB = amtB * (decB - 1) - (isFreeBetA ? 0 : amtA) - amtC;
  const profitDraw = amtC * (decC - 1) - (isFreeBetA ? 0 : amtA) - (isFreeBetB ? 0 : amtB);

  const options = [
    { value: 'A', label: bet.side || 'Side A', profit: profitA },
    ...(isThreeWay ? [{ value: 'Draw', label: 'Draw', profit: profitDraw }] : []),
    ...(bet.sideB ? [{ value: 'B', label: bet.sideB, profit: profitB }] : []),
  ];

  const [winner, setWinner] = useState('A');
  const selected = options.find(o => o.value === winner) || options[0];
  const profit = selected?.profit ?? 0;

  function handleSubmit(e) {
    e.preventDefault();
    onSettle(bet.id, 'win', profit);
  }

  return (
    <form className="bh-settle-form" onSubmit={handleSubmit} onClick={e => e.stopPropagation()}>
      <span className="bh-settle-which">Winner:</span>
      <select value={winner} onChange={e => setWinner(e.target.value)} className="bh-settle-select">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <span className={`bh-settle-profit ${profit >= 0 ? 'bh-pl-pos' : 'bh-pl-neg'}`}>
        {profit >= 0 ? '+' : '-'}${Math.abs(profit).toFixed(2)}
      </span>
      <button type="submit" className="bh-settle-submit">Save</button>
      <button type="button" className="bh-settle-cancel" onClick={onCancel}>✕</button>
    </form>
  );
}

function EditForm({ bet, onSave, onCancel }) {
  const isMultiLeg = bet.sideB != null;
  const [amount, setAmount] = useState(String(bet.amount ?? ''));
  const [odds, setOdds] = useState(String(bet.odds ?? ''));
  const [amountB, setAmountB] = useState(String(bet.amountB ?? ''));
  const [oddsB, setOddsB] = useState(String(bet.oddsB ?? ''));

  async function handleSubmit(e) {
    e.preventDefault();
    const body = {
      amount: parseFloat(amount) || bet.amount,
      odds: parseInt(odds) || bet.odds,
    };
    if (isMultiLeg) {
      body.amountB = parseFloat(amountB) || bet.amountB;
      body.oddsB = parseInt(oddsB) || bet.oddsB;
    }
    await fetch(`${API_BASE}/user/bets/${bet.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bet._token}` },
      body: JSON.stringify(body),
    });
    onSave({ ...bet, ...body });
  }

  return (
    <form className="bh-edit-form" onSubmit={handleSubmit} onClick={e => e.stopPropagation()}>
      <div className="bh-edit-row">
        <div className="bh-edit-field">
          <label>{isMultiLeg ? `${bet.side} odds` : 'Odds'}</label>
          <input className="bh-edit-input" value={odds} onChange={e => setOdds(e.target.value)} autoFocus />
        </div>
        <div className="bh-edit-field">
          <label>{isMultiLeg ? `${bet.side} stake` : 'Stake ($)'}</label>
          <input type="number" className="bh-edit-input" value={amount} onChange={e => setAmount(e.target.value)} step="0.01" />
        </div>
        {isMultiLeg && (
          <>
            <div className="bh-edit-field">
              <label>{bet.sideB} odds</label>
              <input className="bh-edit-input" value={oddsB} onChange={e => setOddsB(e.target.value)} />
            </div>
            <div className="bh-edit-field">
              <label>{bet.sideB} stake</label>
              <input type="number" className="bh-edit-input" value={amountB} onChange={e => setAmountB(e.target.value)} step="0.01" />
            </div>
          </>
        )}
      </div>
      <div className="bh-edit-btns">
        <button type="submit" className="bh-settle-submit">Save</button>
        <button type="button" className="bh-settle-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function BetCard({ bet, onSettle, onDelete, onEdit }) {
  const [settling, setSettling] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isPending = bet.result === 'pending';

  const notes = safeParseNotes(bet.notes);
  const isMultiLeg = bet.sideB != null;
  const isThreeWay = !!(notes?.is_three_way);
  const commenceTime = notes?.commence_time ? new Date(notes.commence_time) : null;
  const isOverdue = isPending && commenceTime && notes?.event_id &&
    (Date.now() - commenceTime) > 24 * 60 * 60 * 1000;

  const propLabel = getPropLabel(bet.market, notes);

  const amtA = bet.amount || 0;
  const amtB = bet.amountB || 0;
  const amtC = isThreeWay ? (notes?.amount_c || 0) : 0;
  const totalStake = amtA + amtB + amtC;

  const decA = oddsToDecimal(bet.odds);
  const decB = oddsToDecimal(bet.oddsB);
  const decC = notes?.odds_c ? oddsToDecimal(notes.odds_c) : 1;
  const isFreeBetA = notes?.promo_type === 'free_bet' || (notes?.promo_type === 'bet_and_get' && notes?.stage !== 'qualifying');
  const isFreeBetB = notes?.is_free_money_b === true;
  const profitIfAWins = amtA * (decA - 1) - (isFreeBetB ? 0 : amtB) - amtC;
  const profitIfBWins = amtB * (decB - 1) - (isFreeBetA ? 0 : amtA) - amtC;
  const profitIfDrawWins = amtC * (decC - 1) - (isFreeBetA ? 0 : amtA) - (isFreeBetB ? 0 : amtB);

  const potentialProfit = !isMultiLeg && isPending && bet.odds && bet.amount
    ? bet.amount * (oddsToDecimal(bet.odds) - 1)
    : null;

  const minProfit = isMultiLeg && isPending
    ? Math.min(profitIfAWins, ...(isThreeWay ? [profitIfDrawWins] : []), profitIfBWins)
    : null;

  async function handleSettle(id, result, profitLoss) {
    const body = { result };
    if (profitLoss != null) body.profitLoss = profitLoss;
    else if (result === 'loss') body.profitLoss = isMultiLeg ? -totalStake : -(bet.amount || 0);
    else if (result === 'push') body.profitLoss = 0;

    await fetch(`${API_BASE}/user/bets/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bet._token}` },
      body: JSON.stringify(body),
    });
    setSettling(false);
    onSettle(id, result, body.profitLoss);
  }

  async function handleDelete() {
    await fetch(`${API_BASE}/user/bets/${bet.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${bet._token}` },
    });
    onDelete(bet.id);
  }

  const hasPL = bet.profitLoss != null;
  const betDesc = describeBet(bet);
  const promoTypeLabel = bet.betType === 'promo' ? PROMO_TYPE_LABELS[notes?.promo_type] : null;
  const marketLabel = getMarketLabel(bet.market, notes, bet.side);

  const selectionText = betDesc || bet.eventName || '—';
  const hasTeamNames = notes?.home_team && notes?.away_team;
  const eventText = hasTeamNames
    ? `${notes.home_team} vs ${notes.away_team}`
    : (!isMultiLeg && bet.eventName && bet.eventName !== selectionText ? bet.eventName : null);

  return (
    <div className={`bh-card bh-card-${bet.result}`}>
      {/* Header: book + type badges */}
      <div className="bh-card-header">
        <div className="bh-header-book">
          {isMultiLeg ? (
            <span className="bh-header-book-name bh-header-multibook">
              {isThreeWay ? '3 Books' : '2 Books'}
            </span>
          ) : (
            <>
              <SportsbookLogo bookKey={bet.sportsbook} size={16} />
              <span className="bh-header-book-name">{bookDisplayName(bet.sportsbook)}</span>
            </>
          )}
        </div>
        <div className="bh-header-badges">
          <span className={`bh-type-badge bh-type-${bet.betType}`}>{TYPE_LABELS[bet.betType] || bet.betType}</span>
          {promoTypeLabel && <span className="bh-promo-type-badge">{promoTypeLabel}</span>}
        </div>
      </div>

      {/* Main body */}
      <div className="bh-card-body">
        {!isMultiLeg ? (
          <>
            <div className="bh-sel-row">
              <span className="bh-sel-text">{selectionText}</span>
              {bet.odds != null && (
                <span className={`bh-odds-pill bh-odds-pill-${bet.result}`}>{formatOdds(bet.odds)}</span>
              )}
            </div>
            {marketLabel && <div className="bh-sel-market">{marketLabel}</div>}
            {(eventText || commenceTime) && (
              <div className="bh-sel-event-row">
                <span className="bh-sel-event">{eventText || ''}</span>
                {commenceTime && (
                  <span className="bh-sel-time">
                    {commenceTime.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </span>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Leg A */}
            <div className="bh-leg-row">
              <div className="bh-leg-book">
                <SportsbookLogo bookKey={bet.sportsbook} size={13} />
                <span className="bh-leg-book-name">{bookDisplayName(bet.sportsbook)}</span>
              </div>
              <span className="bh-leg-side">{withPropLabel(bet.side, propLabel)}</span>
              <span className="bh-leg-odds-new">{formatOdds(bet.odds)}</span>
              <span className="bh-leg-stake-new">${amtA % 1 === 0 ? amtA : amtA.toFixed(2)}</span>
            </div>
            {isThreeWay && (
              <div className="bh-leg-row">
                <div className="bh-leg-book">
                  <SportsbookLogo bookKey={notes.sportsbook_c} size={13} />
                  <span className="bh-leg-book-name">{bookDisplayName(notes.sportsbook_c)}</span>
                </div>
                <span className="bh-leg-side">Draw</span>
                <span className="bh-leg-odds-new">{formatOdds(notes.odds_c)}</span>
                <span className="bh-leg-stake-new">${amtC % 1 === 0 ? amtC : amtC.toFixed(2)}</span>
              </div>
            )}
            {/* Leg B */}
            <div className="bh-leg-row">
              <div className="bh-leg-book">
                <SportsbookLogo bookKey={bet.sportsbookB} size={13} />
                <span className="bh-leg-book-name">{bookDisplayName(bet.sportsbookB)}</span>
              </div>
              <span className="bh-leg-side">{withPropLabel(bet.sideB, propLabel)}</span>
              <span className="bh-leg-odds-new">{formatOdds(bet.oddsB)}</span>
              <span className="bh-leg-stake-new">${amtB % 1 === 0 ? amtB : amtB.toFixed(2)}</span>
            </div>
            {/* Event + time */}
            <div className="bh-sel-event-row bh-legs-event-row">
              <span className="bh-sel-event">{eventText || bet.eventName || ''}</span>
              {commenceTime && (
                <span className="bh-sel-time">
                  {commenceTime.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
            </div>
            {/* Outcomes */}
            {isPending && (
              <div className="bh-outcomes-strip">
                <span className={profitIfAWins >= 0 ? 'bh-outcome-pos' : 'bh-outcome-neg'}>
                  {shortName(bet.side, propLabel)} wins: {profitIfAWins >= 0 ? '+' : '-'}${Math.abs(profitIfAWins).toFixed(2)}
                </span>
                {isThreeWay && (
                  <span className={profitIfDrawWins >= 0 ? 'bh-outcome-pos' : 'bh-outcome-neg'}>
                    Draw: {profitIfDrawWins >= 0 ? '+' : '-'}${Math.abs(profitIfDrawWins).toFixed(2)}
                  </span>
                )}
                <span className={profitIfBWins >= 0 ? 'bh-outcome-pos' : 'bh-outcome-neg'}>
                  {shortName(bet.sideB, propLabel)} wins: {profitIfBWins >= 0 ? '+' : '-'}${Math.abs(profitIfBWins).toFixed(2)}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Wager / Payout row */}
      <div className="bh-wp-row">
        <div className="bh-wp-col">
          <span className="bh-wp-amount">
            ${(isMultiLeg ? totalStake : (bet.amount ?? 0)).toFixed(2)}
          </span>
          <span className="bh-wp-label">TOTAL WAGER</span>
        </div>
        <div className="bh-wp-divider" />
        <div className={`bh-wp-col bh-wp-right ${hasPL ? (bet.profitLoss >= 0 ? 'bh-wp-win' : 'bh-wp-loss') : 'bh-wp-open'}`}>
          {hasPL ? (
            <>
              <span className="bh-wp-amount">
                {bet.profitLoss >= 0
                  ? `+$${bet.profitLoss.toFixed(2)}`
                  : `-$${Math.abs(bet.profitLoss).toFixed(2)}`}
              </span>
              <span className="bh-wp-label">
                {(() => {
                  if (isMultiLeg) {
                    const aWon = Math.abs(bet.profitLoss - profitIfAWins) < 0.10;
                    const bWon = Math.abs(bet.profitLoss - profitIfBWins) < 0.10;
                    if (aWon) return `${shortName(bet.side, propLabel)} WON`;
                    if (bWon) return `${shortName(bet.sideB, propLabel)} WON`;
                  }
                  return bet.profitLoss >= 0 ? 'PROFIT' : 'LOSS';
                })()}
              </span>
            </>
          ) : potentialProfit != null ? (
            <>
              <span className="bh-wp-amount">+${potentialProfit.toFixed(2)}</span>
              <span className="bh-wp-label">TO WIN</span>
            </>
          ) : isMultiLeg && isPending && minProfit != null ? (
            <>
              <span className="bh-wp-amount">
                {minProfit >= 0
                  ? `+$${minProfit.toFixed(2)}`
                  : `-$${Math.abs(minProfit).toFixed(2)}`}
              </span>
              <span className="bh-wp-label">{['arb', 'promo'].includes(bet.betType) ? 'MIN PROFIT' : 'TO WIN'}</span>
            </>
          ) : (
            <>
              <span className="bh-wp-amount">—</span>
              <span className="bh-wp-label">PAYOUT</span>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bh-footer">
        <div className="bh-footer-dates">
          <span className="bh-footer-date">Placed {formatDateTime(bet.placedAt)}</span>
          {commenceTime && (
            <span className="bh-footer-date">Game {formatDateTime(notes.commence_time)}</span>
          )}
        </div>
        <div className="bh-footer-actions">
          {isPending && !settling && !editing && !confirmDelete && (
            <button className="bh-act bh-act-settle" onClick={() => setSettling(true)} title="Settle bet">
              <CheckSquare size={15} />
            </button>
          )}
          {!settling && !editing && !confirmDelete && (
            <button className="bh-act bh-act-edit" onClick={() => setEditing(true)} title="Edit bet">
              <Pencil size={15} />
            </button>
          )}
          {!settling && !editing && !confirmDelete && (
            <button className="bh-act bh-act-delete" onClick={() => setConfirmDelete(true)} title="Delete bet">
              <Trash2 size={15} />
            </button>
          )}
          {confirmDelete && (
            <>
              <button className="bh-delete-confirm" onClick={handleDelete}>Delete</button>
              <button className="bh-delete-cancel" onClick={() => setConfirmDelete(false)}>Keep</button>
            </>
          )}
        </div>
      </div>

      {isOverdue && !settling && !editing && (
        <div className="bh-overdue-banner">
          Game finished but no auto-result — tap <CheckSquare size={12} style={{verticalAlign:'middle'}} /> to settle manually.
        </div>
      )}

      {settling && isMultiLeg ? (
        <ArbSettleForm bet={bet} notes={notes} onSettle={handleSettle} onCancel={() => setSettling(false)} />
      ) : settling ? (
        <SettleForm bet={bet} onSettle={handleSettle} onCancel={() => setSettling(false)} />
      ) : editing ? (
        <EditForm bet={bet} onSave={(updated) => { setEditing(false); onEdit(updated); }} onCancel={() => setEditing(false)} />
      ) : null}
    </div>
  );
}

const DATE_RANGES = [
  { key: 'all', label: 'All Time' },
  { key: '7', label: '7 Days' },
  { key: '14', label: '14 Days' },
  { key: '30', label: '30 Days' },
];

function DepositBonusCard({ rolloverBets, onSettle, onDelete }) {
  const [settlingId, setSettlingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const firstBet = rolloverBets[0];
  if (!firstBet) return null;
  const firstNotes = safeParseNotes(firstBet.notes);
  const promoBook = firstNotes?.promo_book || firstBet.sportsbook;
  const cardBookName = bookDisplayName(promoBook);
  const bonusAmount = firstNotes?.bonus_amount ?? null;
  const totalRollover = firstNotes?.total_rollover;
  const rolledOver = rolloverBets.reduce((sum, b) => sum + (b.amount || 0), 0);
  const progressPct = totalRollover ? Math.min(100, (rolledOver / totalRollover) * 100) : 0;
  const isComplete = totalRollover && rolledOver >= totalRollover;

  const allSettled = rolloverBets.every(b => b.result !== 'pending');

  const totalCostEstimate = rolloverBets.reduce((sum, b) => {
    if (b.profitLoss != null) return sum + b.profitLoss;
    if (b.sideB != null) {
      const decA = oddsToDecimal(b.odds);
      return sum + (b.amount || 0) * (decA - 1) - (b.amountB || 0);
    }
    return sum;
  }, 0);

  const netProfit = (bonusAmount != null && isComplete) ? bonusAmount + totalCostEstimate : null;
  const isEstimate = netProfit != null && !allSettled;
  const cardResult = netProfit != null ? (netProfit > 0 ? 'win' : netProfit < 0 ? 'loss' : 'push') : 'pending';

  return (
    <div className={`bh-card bh-card-${cardResult}`}>
      {/* Header */}
      <div className="bh-card-header">
        <div className="bh-header-book">
          <SportsbookLogo bookKey={promoBook} size={16} />
          <span className="bh-header-book-name">{cardBookName}</span>
        </div>
        <div className="bh-header-badges">
          <span className="bh-type-badge bh-type-promo">Promo</span>
          <span className="bh-promo-type-badge">Deposit Bonus</span>
        </div>
      </div>

      {/* Rollover progress */}
      {totalRollover && (
        <div className="bh-deposit-progress">
          <div className="bh-deposit-progress-header">
            <span>Rolled over: <strong>${rolledOver.toFixed(2)}</strong> / ${totalRollover.toFixed(2)}</span>
            <span className="bh-deposit-progress-pct">{progressPct.toFixed(0)}%{isComplete ? ' — Complete' : ''}</span>
          </div>
          <div className="bh-deposit-progress-bar">
            <div className="bh-deposit-progress-fill" style={{width: `${progressPct}%`}} />
          </div>
        </div>
      )}

      {/* Individual rollover bets */}
      {rolloverBets.map((bet, idx) => {
        const bNotes = safeParseNotes(bet.notes);
        const isMultiLeg = bet.sideB != null;
        const amtA = bet.amount || 0;
        const amtB = bet.amountB || 0;
        const decA = oddsToDecimal(bet.odds);
        const decB = oddsToDecimal(bet.oddsB);
        const profitIfAWins = amtA * (decA - 1) - amtB;
        const profitIfBWins = amtB * (decB - 1) - amtA;

        const effectiveBonus = (bonusAmount != null && totalRollover)
          ? (amtA / totalRollover) * bonusAmount
          : null;
        const netIfAWins = effectiveBonus != null ? effectiveBonus + profitIfAWins : null;
        const netIfBWins = effectiveBonus != null ? effectiveBonus + profitIfBWins : null;
        const betPropLabel = getPropLabel(bet.market, bNotes);

        return (
          <div key={bet.id} className="bh-bag-part">
            <div className="bh-bag-part-header">
              <span className="bh-bag-part-label">Rollover Bet {idx + 1}</span>
              <span className={`bh-result-pill bh-result-${bet.result}`}>{RESULT_LABELS[bet.result] || bet.result}</span>
            </div>

            {isMultiLeg ? (
              <div className="bh-legs bh-bag-legs">
                <div className="bh-leg">
                  <span className="bh-leg-side">{bet.side}</span>
                  <span className="bh-leg-book">
                    <SportsbookLogo bookKey={bet.sportsbook} size={14} />
                    {bookDisplayName(bet.sportsbook)}
                  </span>
                  <span className="bh-leg-odds">{formatOdds(bet.odds)}</span>
                  <span className="bh-leg-stake">${amtA % 1 === 0 ? amtA : amtA.toFixed(2)}</span>
                </div>
                <div className="bh-leg">
                  <span className="bh-leg-side">{bet.sideB}</span>
                  <span className="bh-leg-book">
                    <SportsbookLogo bookKey={bet.sportsbookB} size={14} />
                    {bookDisplayName(bet.sportsbookB)}
                  </span>
                  <span className="bh-leg-odds">{formatOdds(bet.oddsB)}</span>
                  <span className="bh-leg-stake">${amtB % 1 === 0 ? amtB : amtB.toFixed(2)}</span>
                </div>
                {bet.result === 'pending' && bet.sideB != null && netIfAWins != null && netIfBWins != null && (
                  <div className="bh-outcomes">
                    <span className={netIfAWins >= 0 ? 'bh-outcome-pos' : 'bh-outcome-neg'}>
                      {shortName(bet.side, betPropLabel)} wins: {netIfAWins >= 0 ? '+' : '-'}${Math.abs(netIfAWins).toFixed(2)}
                    </span>
                    <span className={netIfBWins >= 0 ? 'bh-outcome-pos' : 'bh-outcome-neg'}>
                      {shortName(bet.sideB, betPropLabel)} wins: {netIfBWins >= 0 ? '+' : '-'}${Math.abs(netIfBWins).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="bh-bag-part-detail">
                <span className="bh-bag-part-event">{bet.eventName}</span>
                {bet.side && <span className="bh-bag-part-side">{bet.side}</span>}
                <span className="bh-bag-part-odds">{formatOdds(bet.odds)}</span>
                <span className="bh-bag-part-stake">${bet.amount != null ? bet.amount : '—'}</span>
              </div>
            )}

            {bet.profitLoss != null && (
              <div className="bh-bag-part-pl-row">
                <span className="bh-deposit-cost-label">Rollover cost:</span>
                <span className="bh-deposit-cost-val">{formatPL(bet.profitLoss)}</span>
              </div>
            )}

            <div className="bh-deposit-part-footer">
              <span className="bh-bag-part-date">Placed {formatDateTime(bet.placedAt)}</span>
              {bet.result === 'pending' && settlingId !== bet.id && (
                <button className="bh-settle-btn" onClick={() => setSettlingId(bet.id)}>Settle</button>
              )}
            </div>

            {settlingId === bet.id && (
              isMultiLeg ? (
                <ArbSettleForm bet={bet} notes={bNotes}
                  onSettle={(id, result, pl) => { onSettle(id, result, pl); setSettlingId(null); }}
                  onCancel={() => setSettlingId(null)} />
              ) : (
                <SettleForm bet={bet}
                  onSettle={(id, result, pl) => { onSettle(id, result, pl); setSettlingId(null); }}
                  onCancel={() => setSettlingId(null)} />
              )
            )}
          </div>
        );
      })}

      {/* Wager / net profit */}
      <div className="bh-wp-row">
        <div className="bh-wp-col">
          <span className="bh-wp-amount">${rolledOver.toFixed(2)}</span>
          <span className="bh-wp-label">TOTAL WAGERED</span>
        </div>
        <div className="bh-wp-divider" />
        <div className={`bh-wp-col bh-wp-right ${netProfit != null && !isEstimate ? (netProfit >= 0 ? 'bh-wp-win' : 'bh-wp-loss') : 'bh-wp-open'}`}>
          {netProfit != null && !isEstimate ? (
            <>
              <span className="bh-wp-amount">
                {netProfit >= 0 ? `+$${netProfit.toFixed(2)}` : `-$${Math.abs(netProfit).toFixed(2)}`}
              </span>
              <span className="bh-wp-label">NET PROFIT</span>
            </>
          ) : bonusAmount != null ? (
            <>
              <span className="bh-wp-amount">${bonusAmount}</span>
              <span className="bh-wp-label">BONUS TARGET</span>
            </>
          ) : (
            <>
              <span className="bh-wp-amount">—</span>
              <span className="bh-wp-label">BONUS</span>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bh-footer">
        <div className="bh-footer-dates">
          <span className="bh-footer-date">{rolloverBets.length} rollover bet{rolloverBets.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="bh-footer-actions">
          {!confirmDelete ? (
            <button className="bh-act bh-act-delete" onClick={() => setConfirmDelete(true)} title="Delete all rollover bets">
              <Trash2 size={15} />
            </button>
          ) : (
            <>
              <button className="bh-delete-confirm" onClick={() => onDelete && onDelete(rolloverBets.map(b => b.id))}>Delete</button>
              <button className="bh-delete-cancel" onClick={() => setConfirmDelete(false)}>Keep</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function NoSweatCard({ bet, onSettle, onDelete, onEdit, onNavigate }) {
  const notes = safeParseNotes(bet.notes);
  const isPending = bet.result === 'pending';
  const [settling, setSettling] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  let sideAWon = null;
  if (!isPending && bet.sideB != null && bet.profitLoss != null) {
    const decA = oddsToDecimal(bet.odds);
    const decB = oddsToDecimal(bet.oddsB);
    const profitIfAWins = (bet.amount || 0) * (decA - 1) - (bet.amountB || 0);
    const profitIfBWins = (bet.amountB || 0) * (decB - 1) - (bet.amount || 0);
    if (Math.abs(bet.profitLoss - profitIfAWins) < 1) sideAWon = true;
    else if (Math.abs(bet.profitLoss - profitIfBWins) < 1) sideAWon = false;
  }

  const cardResult = isPending ? 'pending' : (bet.result || 'pending');
  const bookName = bookDisplayName(bet.sportsbook);
  const nsCommenceTime = notes?.commence_time ? new Date(notes.commence_time) : null;
  const isOverdue = isPending && nsCommenceTime && notes?.event_id &&
    (Date.now() - nsCommenceTime) > 24 * 60 * 60 * 1000;

  const totalStake = (bet.amount || 0) + (bet.amountB || 0);

  return (
    <div className={`bh-card bh-card-${cardResult}`}>
      {/* Header */}
      <div className="bh-card-header">
        <div className="bh-header-book">
          <SportsbookLogo bookKey={bet.sportsbook} size={16} />
          <span className="bh-header-book-name">{bookName}</span>
        </div>
        <div className="bh-header-badges">
          <span className="bh-type-badge bh-type-promo">Promo</span>
          <span className="bh-promo-type-badge">No Sweat</span>
        </div>
      </div>

      {/* Part 1 */}
      <div className="bh-bag-part">
        <div className="bh-bag-part-header">
          <span className="bh-bag-part-label">Part 1 — No-Sweat Bet</span>
          <span className={`bh-result-pill bh-result-${bet.result}`}>{RESULT_LABELS[bet.result] || bet.result}</span>
        </div>
        {bet.sideB != null ? (
          <div className="bh-legs bh-bag-legs">
            <div className="bh-leg">
              <span className="bh-leg-side">{bet.side}</span>
              <span className="bh-leg-book">
                <SportsbookLogo bookKey={bet.sportsbook} size={14} />
                {bookDisplayName(bet.sportsbook)}
              </span>
              <span className="bh-leg-odds">{bet.odds > 0 ? `+${bet.odds}` : bet.odds}</span>
              <span className="bh-leg-stake">${bet.amount != null ? bet.amount : '—'}</span>
            </div>
            <div className="bh-leg">
              <span className="bh-leg-side">{bet.sideB}</span>
              <span className="bh-leg-book">
                <SportsbookLogo bookKey={bet.sportsbookB} size={14} />
                {bookDisplayName(bet.sportsbookB)}
              </span>
              <span className="bh-leg-odds">{bet.oddsB > 0 ? `+${bet.oddsB}` : bet.oddsB}</span>
              <span className="bh-leg-stake">${bet.amountB != null ? Number(bet.amountB).toFixed(2) : '—'}</span>
            </div>
          </div>
        ) : (
          <div className="bh-bag-part-detail">
            <span className="bh-bag-part-event">{bet.eventName}</span>
            <span className="bh-bag-part-odds">{bet.odds > 0 ? `+${bet.odds}` : bet.odds}</span>
            <span className="bh-bag-part-stake">${bet.amount || '—'}</span>
          </div>
        )}
        <div className="bh-bag-part-date">Placed {formatDateTime(bet.placedAt)}</div>
      </div>

      {/* Part 2 */}
      <div className="bh-bag-part">
        <div className="bh-bag-part-header">
          <span className="bh-bag-part-label">Part 2 — Free Bet</span>
          <span className="bh-result-pill bh-result-pending">
            {isPending ? 'Awaiting' : sideAWon === true ? 'Not needed' : sideAWon === false ? 'Available' : 'Awaiting'}
          </span>
        </div>
        <div className="bh-bag-part-detail">
          {isPending ? (
            <span className="bh-bag-ns-note">Waiting for initial bet to settle…</span>
          ) : sideAWon === true ? (
            <span className="bh-bag-ns-note bh-bag-ns-won">✓ No-sweat bet won — no free bet needed.</span>
          ) : sideAWon === false ? (
            <>
              <span className="bh-bag-ns-note">Initial bet lost — your ${bet.amount} free bet is ready to convert.</span>
              {onNavigate && (
                <button className="bh-bag-goto-promo" onClick={() => onNavigate('/promo')}>
                  Go make bonus bet →
                </button>
              )}
            </>
          ) : (
            <span className="bh-bag-ns-note">Check your account for a free bet.</span>
          )}
        </div>
      </div>

      {isOverdue && !settling && (
        <div className="bh-overdue-banner">
          Game finished but no auto-result — tap <CheckSquare size={12} style={{verticalAlign:'middle'}} /> to settle manually.
        </div>
      )}

      {settling && (
        bet.sideB != null ? (
          <ArbSettleForm bet={bet} notes={notes} onSettle={(id, result, pl) => { onSettle(id, result, pl); setSettling(false); }} onCancel={() => setSettling(false)} />
        ) : (
          <SettleForm bet={bet} onSettle={(id, result, pl) => { onSettle(id, result, pl); setSettling(false); }} onCancel={() => setSettling(false)} />
        )
      )}

      {/* Wager / P&L */}
      <div className="bh-wp-row">
        <div className="bh-wp-col">
          <span className="bh-wp-amount">${totalStake.toFixed(2)}</span>
          <span className="bh-wp-label">TOTAL WAGER</span>
        </div>
        <div className="bh-wp-divider" />
        <div className={`bh-wp-col bh-wp-right ${!isPending && bet.profitLoss != null ? (bet.profitLoss >= 0 ? 'bh-wp-win' : 'bh-wp-loss') : 'bh-wp-open'}`}>
          {!isPending && bet.profitLoss != null ? (
            <>
              <span className="bh-wp-amount">
                {bet.profitLoss >= 0 ? `+$${bet.profitLoss.toFixed(2)}` : `-$${Math.abs(bet.profitLoss).toFixed(2)}`}
              </span>
              <span className="bh-wp-label">{bet.profitLoss >= 0 ? 'PROFIT' : 'LOSS'}</span>
            </>
          ) : (
            <>
              <span className="bh-wp-amount">—</span>
              <span className="bh-wp-label">IN PROGRESS</span>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bh-footer">
        <div className="bh-footer-dates">
          <span className="bh-footer-date">Placed {formatDateTime(bet.placedAt)}</span>
        </div>
        <div className="bh-footer-actions">
          {isPending && !settling && (
            <button className="bh-act bh-act-settle" onClick={() => setSettling(true)} title="Settle bet">
              <CheckSquare size={15} />
            </button>
          )}
          {!confirmDelete ? (
            <button className="bh-act bh-act-delete" onClick={() => setConfirmDelete(true)} title="Delete bet">
              <Trash2 size={15} />
            </button>
          ) : (
            <>
              <button className="bh-delete-confirm" onClick={() => onDelete(bet.id)}>Delete</button>
              <button className="bh-delete-cancel" onClick={() => setConfirmDelete(false)}>Keep</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BetAndGetCard({ qualifyingBet, bonusBet, onNavigate, onSettle, onDelete }) {
  const [settlingPart, setSettlingPart] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const qNotes = safeParseNotes(qualifyingBet.notes);
  const bNotes = bonusBet ? safeParseNotes(bonusBet.notes) : null;

  const qPL = qualifyingBet.profitLoss;
  const bPL = bonusBet?.profitLoss;
  const hasCombinedPL = qPL != null && bPL != null;
  const combinedPL = (qPL ?? 0) + (bPL ?? 0);

  const cardResult = hasCombinedPL
    ? (combinedPL > 0 ? 'win' : combinedPL < 0 ? 'loss' : 'push')
    : 'pending';
  const bookName = bookDisplayName(qualifyingBet.sportsbook);

  const totalStake = (qualifyingBet.amount || 0) + (qualifyingBet.amountB || 0) +
    (bonusBet ? (bonusBet.amount || 0) + (bonusBet.amountB || 0) : 0);

  return (
    <div className={`bh-card bh-card-${cardResult}`}>
      {/* Header */}
      <div className="bh-card-header">
        <div className="bh-header-book">
          <SportsbookLogo bookKey={qualifyingBet.sportsbook} size={16} />
          <span className="bh-header-book-name">{bookName}</span>
        </div>
        <div className="bh-header-badges">
          <span className="bh-type-badge bh-type-promo">Promo</span>
          <span className="bh-promo-type-badge">Bet & Get</span>
        </div>
      </div>

      {/* Part 1 */}
      <div className="bh-bag-part">
        <div className="bh-bag-part-header">
          <span className="bh-bag-part-label">Part 1 — Qualifying Bet</span>
          <span className={`bh-result-pill bh-result-${qualifyingBet.result}`}>{RESULT_LABELS[qualifyingBet.result] || qualifyingBet.result}</span>
        </div>
        {qualifyingBet.sideB != null ? (() => {
          const qAmtA = qualifyingBet.amount || 0;
          const qAmtB = qualifyingBet.amountB || 0;
          const qDecA = oddsToDecimal(qualifyingBet.odds);
          const qDecB = oddsToDecimal(qualifyingBet.oddsB);
          const qIsFreeBetA = qNotes?.promo_type === 'free_bet';
          const qIsFreeBetB = qNotes?.is_free_money_b === true;
          const qPropLabel = getPropLabel(qualifyingBet.market, qNotes);
          const qProfitIfAWins = qAmtA * (qDecA - 1) - (qIsFreeBetB ? 0 : qAmtB);
          const qProfitIfBWins = qAmtB * (qDecB - 1) - (qIsFreeBetA ? 0 : qAmtA);
          return (
            <div className="bh-legs bh-bag-legs">
              <div className="bh-leg">
                <span className="bh-leg-side">{qualifyingBet.side}</span>
                <span className="bh-leg-book">
                  <SportsbookLogo bookKey={qualifyingBet.sportsbook} size={14} />
                  {bookDisplayName(qualifyingBet.sportsbook)}
                </span>
                <span className="bh-leg-odds">{formatOdds(qualifyingBet.odds)}</span>
                <span className="bh-leg-stake">${qualifyingBet.amount != null ? qualifyingBet.amount : '—'}</span>
              </div>
              <div className="bh-leg">
                <span className="bh-leg-side">{qualifyingBet.sideB}</span>
                <span className="bh-leg-book">
                  <SportsbookLogo bookKey={qualifyingBet.sportsbookB} size={14} />
                  {bookDisplayName(qualifyingBet.sportsbookB)}
                </span>
                <span className="bh-leg-odds">{formatOdds(qualifyingBet.oddsB)}</span>
                <span className="bh-leg-stake">${qualifyingBet.amountB != null ? qualifyingBet.amountB?.toFixed(2) : '—'}</span>
              </div>
              {qualifyingBet.result === 'pending' && (
                <div className="bh-outcomes">
                  <span className={qProfitIfAWins >= 0 ? 'bh-outcome-pos' : 'bh-outcome-neg'}>
                    {shortName(qualifyingBet.side, qPropLabel)} wins: {qProfitIfAWins >= 0 ? '+' : '-'}${Math.abs(qProfitIfAWins).toFixed(2)}
                  </span>
                  <span className={qProfitIfBWins >= 0 ? 'bh-outcome-pos' : 'bh-outcome-neg'}>
                    {shortName(qualifyingBet.sideB, qPropLabel)} wins: {qProfitIfBWins >= 0 ? '+' : '-'}${Math.abs(qProfitIfBWins).toFixed(2)}
                  </span>
                </div>
              )}
              {qPL != null && (
                <div className="bh-bag-part-pl-row">
                  <span className={qPL >= 0 ? 'bh-pl-pos' : 'bh-pl-neg'}>{formatPL(qPL)}</span>
                </div>
              )}
            </div>
          );
        })() : (
          <div className="bh-bag-part-detail">
            <span className="bh-bag-part-event">{qualifyingBet.eventName}</span>
            {qualifyingBet.side && <span className="bh-bag-part-side">{qualifyingBet.side}</span>}
            <span className="bh-bag-part-odds">{formatOdds(qualifyingBet.odds)}</span>
            <span className="bh-bag-part-stake">${qualifyingBet.amount != null ? qualifyingBet.amount : '—'}</span>
            {qPL != null && (
              <span className={`bh-bag-part-pl ${qPL >= 0 ? 'bh-pl-pos' : 'bh-pl-neg'}`}>{formatPL(qPL)}</span>
            )}
          </div>
        )}
        <div className="bh-bag-part-date">Placed {formatDateTime(qualifyingBet.placedAt)}</div>
      </div>

      {/* Part 2 */}
      <div className="bh-bag-part">
        <div className="bh-bag-part-header">
          <span className="bh-bag-part-label">Part 2 — Bonus Bet</span>
          {bonusBet ? (
            <span className={`bh-result-pill bh-result-${bonusBet.result}`}>{RESULT_LABELS[bonusBet.result] || bonusBet.result}</span>
          ) : (
            <span className="bh-result-pill bh-result-pending">Awaiting</span>
          )}
        </div>
        {bonusBet ? (
          <>
            {bonusBet.sideB != null ? (() => {
              const bAmtA = bonusBet.amount || 0;
              const bAmtB = bonusBet.amountB || 0;
              const bDecA = oddsToDecimal(bonusBet.odds);
              const bDecB = oddsToDecimal(bonusBet.oddsB);
              const bIsFreeBetA = bNotes?.promo_type === 'free_bet' || bNotes?.promo_type === 'bet_and_get';
              const bPropLabel = getPropLabel(bonusBet.market, bNotes);
              const bIsFreeBetB = bNotes?.is_free_money_b === true;
              const profitIfAWins = bAmtA * (bDecA - 1) - (bIsFreeBetB ? 0 : bAmtB);
              const profitIfBWins = bAmtB * (bDecB - 1) - (bIsFreeBetA ? 0 : bAmtA);
              return (
                <div className="bh-legs bh-bag-legs">
                  <div className="bh-leg">
                    <span className="bh-leg-side">{bonusBet.side}</span>
                    <span className="bh-leg-book">
                      <SportsbookLogo bookKey={bonusBet.sportsbook} size={14} />
                      {bookDisplayName(bonusBet.sportsbook)}
                    </span>
                    <span className="bh-leg-odds">{formatOdds(bonusBet.odds)}</span>
                    <span className="bh-leg-stake">${bonusBet.amount != null ? bonusBet.amount : '—'}</span>
                  </div>
                  <div className="bh-leg">
                    <span className="bh-leg-side">{bonusBet.sideB}</span>
                    <span className="bh-leg-book">
                      <SportsbookLogo bookKey={bonusBet.sportsbookB} size={14} />
                      {bookDisplayName(bonusBet.sportsbookB)}
                    </span>
                    <span className="bh-leg-odds">{formatOdds(bonusBet.oddsB)}</span>
                    <span className="bh-leg-stake">${bonusBet.amountB != null ? bonusBet.amountB.toFixed(2) : '—'}</span>
                  </div>
                  {bonusBet.result === 'pending' && (
                    <div className="bh-outcomes">
                      <span className={profitIfAWins >= 0 ? 'bh-outcome-pos' : 'bh-outcome-neg'}>
                        {shortName(bonusBet.side, bPropLabel)} wins: {profitIfAWins >= 0 ? '+' : '-'}${Math.abs(profitIfAWins).toFixed(2)}
                      </span>
                      <span className={profitIfBWins >= 0 ? 'bh-outcome-pos' : 'bh-outcome-neg'}>
                        {shortName(bonusBet.sideB, bPropLabel)} wins: {profitIfBWins >= 0 ? '+' : '-'}${Math.abs(profitIfBWins).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {bPL != null && (
                    <div className="bh-bag-part-pl-row">
                      <span className={bPL >= 0 ? 'bh-pl-pos' : 'bh-pl-neg'}>{formatPL(bPL)}</span>
                    </div>
                  )}
                </div>
              );
            })() : (
              <div className="bh-bag-part-detail">
                <span className="bh-bag-part-event">{bonusBet.eventName}</span>
                {bonusBet.side && <span className="bh-bag-part-side">{bonusBet.side}</span>}
                <span className="bh-bag-part-odds">{formatOdds(bonusBet.odds)}</span>
                <span className="bh-bag-part-stake">${bonusBet.amount != null ? bonusBet.amount : '—'}</span>
                {bPL != null && (
                  <span className={`bh-bag-part-pl ${bPL >= 0 ? 'bh-pl-pos' : 'bh-pl-neg'}`}>{formatPL(bPL)}</span>
                )}
              </div>
            )}
            <div className="bh-bag-part-date">Placed {formatDateTime(bonusBet.placedAt)}</div>
          </>
        ) : (
          <div className="bh-bag-awaiting">
            <span>Bonus bet not yet placed</span>
            {onNavigate && (
              <button className="bh-bag-goto-promo" onClick={() => onNavigate('/promo')}>
                Place Bonus Bet →
              </button>
            )}
          </div>
        )}
      </div>

      {settlingPart === 'q' && (qualifyingBet.sideB != null ? (
        <ArbSettleForm bet={qualifyingBet} notes={qNotes} onSettle={(id, result, pl) => { onSettle(id, result, pl); setSettlingPart(null); }} onCancel={() => setSettlingPart(null)} />
      ) : (
        <SettleForm bet={qualifyingBet} onSettle={(id, result, pl) => { onSettle(id, result, pl); setSettlingPart(null); }} onCancel={() => setSettlingPart(null)} />
      ))}
      {settlingPart === 'b' && bonusBet && (bonusBet.sideB != null ? (
        <ArbSettleForm bet={bonusBet} notes={bNotes} onSettle={(id, result, pl) => { onSettle(id, result, pl); setSettlingPart(null); }} onCancel={() => setSettlingPart(null)} />
      ) : (
        <SettleForm bet={bonusBet} onSettle={(id, result, pl) => { onSettle(id, result, pl); setSettlingPart(null); }} onCancel={() => setSettlingPart(null)} />
      ))}

      {/* Wager / P&L */}
      <div className="bh-wp-row">
        <div className="bh-wp-col">
          <span className="bh-wp-amount">${totalStake.toFixed(2)}</span>
          <span className="bh-wp-label">TOTAL WAGER</span>
        </div>
        <div className="bh-wp-divider" />
        <div className={`bh-wp-col bh-wp-right ${hasCombinedPL ? (combinedPL >= 0 ? 'bh-wp-win' : 'bh-wp-loss') : 'bh-wp-open'}`}>
          {hasCombinedPL ? (
            <>
              <span className="bh-wp-amount">
                {combinedPL >= 0 ? `+$${combinedPL.toFixed(2)}` : `-$${Math.abs(combinedPL).toFixed(2)}`}
              </span>
              <span className="bh-wp-label">{combinedPL >= 0 ? 'TOTAL PROFIT' : 'TOTAL LOSS'}</span>
            </>
          ) : (
            <>
              <span className="bh-wp-amount">—</span>
              <span className="bh-wp-label">IN PROGRESS</span>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bh-footer">
        <div className="bh-footer-dates">
          <span className="bh-footer-date">Placed {formatDateTime(qualifyingBet.placedAt)}</span>
        </div>
        <div className="bh-footer-actions">
          {qualifyingBet.result === 'pending' && settlingPart !== 'q' && (
            <button className="bh-act bh-act-settle" onClick={() => setSettlingPart('q')} title="Settle Part 1">
              <CheckSquare size={15} />
            </button>
          )}
          {bonusBet?.result === 'pending' && settlingPart !== 'b' && (
            <button className="bh-act bh-act-settle" onClick={() => setSettlingPart('b')} title="Settle Part 2">
              <CheckSquare size={15} />
            </button>
          )}
          {!confirmDelete ? (
            <button className="bh-act bh-act-delete" onClick={() => setConfirmDelete(true)} title="Delete">
              <Trash2 size={15} />
            </button>
          ) : (
            <>
              <button className="bh-delete-confirm" onClick={() => onDelete && onDelete([qualifyingBet.id, bonusBet?.id].filter(Boolean))}>Delete</button>
              <button className="bh-delete-cancel" onClick={() => setConfirmDelete(false)}>Keep</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BetHistory({ session, onNavigate }) {
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [resultFilter, setResultFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');

  const load = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/user/bets`, { headers: { Authorization: `Bearer ${session.access_token}` } });
      const data = await res.json();
      if (data.bets) {
        setBets(data.bets.map(b => ({ ...b, _token: session.access_token })));
      }
    } catch (err) {
      console.error('BetHistory load error:', err);
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => { load(); }, [load]);

  async function handleSettle(id, result, profitLoss) {
    const body = { result };
    if (profitLoss != null) body.profitLoss = profitLoss;
    await fetch(`${API_BASE}/user/bets/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify(body),
    });
    setBets(prev => prev.map(b => b.id === id ? { ...b, result, profitLoss } : b));
  }

  async function handleDelete(idOrIds) {
    const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
    await Promise.all(ids.map(id =>
      fetch(`${API_BASE}/user/bets/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
    ));
    setBets(prev => prev.filter(b => !ids.includes(b.id)));
  }

  function handleEdit(updated) {
    setBets(prev => prev.map(b => b.id === updated.id ? { ...b, ...updated } : b));
  }

  const dateRangeCutoff = dateRange === 'all' ? null
    : new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000);

  const bagBonusBets = bets.filter(b => {
    const n = safeParseNotes(b.notes);
    return n?.promo_type === 'bet_and_get' && n?.stage === 'bonus' && n?.qualifying_bet_id;
  });
  const bagBonusMap = new Map();
  bagBonusBets.forEach(b => {
    const n = safeParseNotes(b.notes);
    bagBonusMap.set(Number(n.qualifying_bet_id), b);
  });
  const bagQualifyingBets = bets.filter(b => {
    const n = safeParseNotes(b.notes);
    return n?.promo_type === 'bet_and_get' && n?.stage === 'qualifying';
  });
  const bagQualifyingIds = new Set(bagQualifyingBets.map(b => b.id));
  const bagBonusIds = new Set(bagBonusBets.map(b => b.id));

  const depositRolloverBets = bets.filter(b => {
    const n = safeParseNotes(b.notes);
    return n?.stage === 'deposit_rollover' && n?.promo_id;
  });
  const depositGroupMap = new Map();
  depositRolloverBets.forEach(b => {
    const n = safeParseNotes(b.notes);
    const pid = String(n.promo_id);
    if (!depositGroupMap.has(pid)) depositGroupMap.set(pid, []);
    depositGroupMap.get(pid).push(b);
  });
  const depositGroups = [...depositGroupMap.values()];
  const depositRolloverIds = new Set(depositRolloverBets.map(b => b.id));

  const noSweatBets = bets.filter(b => {
    const n = safeParseNotes(b.notes);
    return n?.promo_type === 'no_sweat' && n?.stage === 'initial' && b.sideB != null;
  });
  const noSweatIds = new Set(noSweatBets.map(b => b.id));

  const regularBets = bets.filter(b =>
    !bagQualifyingIds.has(b.id) &&
    !bagBonusIds.has(b.id) &&
    !depositRolloverIds.has(b.id) &&
    !noSweatIds.has(b.id)
  );

  const bagPairs = bagQualifyingBets.map(qb => ({
    qualifyingBet: qb,
    bonusBet: bagBonusMap.get(qb.id) ?? null,
  }));

  function matchesBagFilter(pair) {
    const qb = pair.qualifyingBet;
    const bb = pair.bonusBet;
    if (resultFilter === 'pending' && qb.result !== 'pending' && (!bb || bb.result !== 'pending')) return false;
    if (resultFilter === 'settled' && qb.result === 'pending' && (!bb || bb.result === 'pending')) return false;
    if (dateRangeCutoff && qb.placedAt && parseUtc(qb.placedAt) < dateRangeCutoff) return false;
    return true;
  }

  const filteredNoSweat = noSweatBets.filter(b => {
    if (typeFilter !== 'all' && typeFilter !== 'promo_lock') return false;
    if (resultFilter === 'pending' && b.result !== 'pending') return false;
    if (resultFilter === 'settled' && b.result === 'pending') return false;
    if (dateRangeCutoff && b.placedAt && parseUtc(b.placedAt) < dateRangeCutoff) return false;
    return true;
  });

  const filtered = regularBets.filter(b => {
    if (typeFilter === 'bet_and_get' || typeFilter === 'deposit_bonus') return false;
    if (typeFilter === 'promo_lock') {
      if (b.betType !== 'promo' || b.sideB == null) return false;
    } else if (typeFilter === 'promo_best') {
      if (b.betType !== 'promo' || b.sideB != null) return false;
    } else if (typeFilter !== 'all' && b.betType !== typeFilter) return false;
    if (resultFilter === 'pending' && b.result !== 'pending') return false;
    if (resultFilter === 'settled' && b.result === 'pending') return false;
    if (dateRangeCutoff && b.placedAt && parseUtc(b.placedAt) < dateRangeCutoff) return false;
    return true;
  });

  const filteredPairs = bagPairs.filter(pair => {
    if (typeFilter !== 'all' && typeFilter !== 'promo_lock') return false;
    return matchesBagFilter(pair);
  });

  const filteredDepositGroups = depositGroups.filter(group => {
    if (typeFilter !== 'all' && typeFilter !== 'promo_lock') return false;
    const firstBet = group[0];
    if (!firstBet) return false;
    const hasPending = group.some(b => b.result === 'pending');
    const hasSettled = group.some(b => b.result !== 'pending');
    if (resultFilter === 'pending' && !hasPending) return false;
    if (resultFilter === 'settled' && !hasSettled) return false;
    if (dateRangeCutoff && firstBet.placedAt && parseUtc(firstBet.placedAt) < dateRangeCutoff) return false;
    return true;
  });

  const depositGroupBets = filteredDepositGroups.flatMap(g => g);
  const allSettledBets = [
    ...filtered.filter(b => ['win', 'loss', 'push'].includes(b.result)),
    ...filteredPairs.flatMap(p => {
      const settled = [];
      if (['win', 'loss', 'push'].includes(p.qualifyingBet.result)) settled.push(p.qualifyingBet);
      if (p.bonusBet && ['win', 'loss', 'push'].includes(p.bonusBet.result)) settled.push(p.bonusBet);
      return settled;
    }),
    ...depositGroupBets.filter(b => ['win', 'loss', 'push'].includes(b.result)),
  ];
  const allPendingBets = [
    ...filtered.filter(b => b.result === 'pending'),
    ...filteredPairs.flatMap(p => {
      const pending = [];
      if (p.qualifyingBet.result === 'pending') pending.push(p.qualifyingBet);
      if (p.bonusBet?.result === 'pending') pending.push(p.bonusBet);
      return pending;
    }),
    ...depositGroupBets.filter(b => b.result === 'pending'),
  ];
  const settledFiltered = allSettledBets;
  const pendingFiltered = allPendingBets;
  const winCount = allSettledBets.filter(b => b.result === 'win').length;
  const lossCount = allSettledBets.filter(b => b.result === 'loss').length;
  const totalPL = settledFiltered.reduce((sum, b) => sum + (b.profitLoss || 0), 0);
  const settledWagered = settledFiltered.reduce((sum, b) => sum + (b.amount || 0) + (b.amountB || 0), 0);
  const pendingWagered = pendingFiltered.reduce((sum, b) => sum + (b.amount || 0) + (b.amountB || 0), 0);
  const totalTracked = filtered.length + filteredPairs.length + filteredDepositGroups.length;
  const winRate = (winCount + lossCount) > 0 ? (winCount / (winCount + lossCount)) * 100 : null;
  const roi = settledWagered > 0 ? (totalPL / settledWagered) * 100 : null;

  const heroHasData = settledFiltered.length > 0;

  return (
    <div className="bet-history">
      <div className="bh-hero">
        <div className="bh-hero-inner">
          <div className="bh-hero-top">
            <div className="bh-hero-label">My Bets</div>
          </div>
          <div className="bh-hero-pl-label">Total P&L</div>
          <div className={`bh-hero-pl ${!heroHasData ? 'bh-hero-pl-empty' : totalPL >= 0 ? 'bh-hero-pl-pos' : 'bh-hero-pl-neg'}`}>
            {heroHasData ? formatPL(totalPL) : '—'}
          </div>
          <div className="bh-hero-secondary">
            <div className="bh-hero-stat">
              <div className={`bh-hero-stat-val ${roi != null ? (roi >= 0 ? 'bh-hero-pos' : 'bh-hero-neg') : ''}`}>
                {roi != null ? `${roi >= 0 ? '+' : ''}${roi.toFixed(1)}%` : '—'}
              </div>
              <div className="bh-hero-stat-lbl">ROI</div>
            </div>
            <div className="bh-hero-div" />
            <div className="bh-hero-stat">
              <div className={`bh-hero-stat-val ${winRate != null ? (winRate >= 50 ? 'bh-hero-pos' : 'bh-hero-neg') : ''}`}>
                {winRate != null ? `${winRate.toFixed(1)}%` : '—'}
              </div>
              <div className="bh-hero-stat-lbl">Win Rate</div>
            </div>
            <div className="bh-hero-div" />
            <div className="bh-hero-stat">
              <div className="bh-hero-stat-val">{totalTracked > 0 ? totalTracked : '—'}</div>
              <div className="bh-hero-stat-lbl">Bets Tracked</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bh-filters">
        <div className="bh-filter-group">
          {DATE_RANGES.map(({ key, label }) => (
            <button
              key={key}
              className={`bh-filter-btn${dateRange === key ? ' active' : ''}`}
              onClick={() => setDateRange(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="bh-filter-group">
          {[
            { key: 'all', label: 'All Types' },
            { key: 'ev', label: '+EV' },
            { key: 'arb', label: 'Arb' },
            { key: 'promo_lock', label: 'Promo - Lock It In' },
            { key: 'promo_best', label: 'Promo - Best Bet' },
            { key: 'manual', label: 'Manual' },
            { key: 'parlay', label: 'Parlay' },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`bh-filter-btn${typeFilter === key ? ' active' : ''}`}
              onClick={() => setTypeFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="bh-filter-group">
          {[
            { key: 'all', label: 'All Bets' },
            { key: 'pending', label: 'Open' },
            { key: 'settled', label: 'Settled' },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`bh-filter-btn${resultFilter === key ? ' active' : ''}`}
              onClick={() => setResultFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="bh-empty">Loading...</div>
      ) : filtered.length === 0 && filteredPairs.length === 0 && filteredDepositGroups.length === 0 && filteredNoSweat.length === 0 ? (
        <div className="bh-empty">
          {bets.length === 0
            ? 'No bets tracked yet. Click "Track Bet" on any +EV or Promo opportunity.'
            : 'No bets match the current filter.'}
        </div>
      ) : (
        <div className="bh-cards">
          {[
            ...filteredDepositGroups.map(group => ({ type: 'deposit', key: group[0].id, sortDate: parseUtc(group[0].placedAt), data: group })),
            ...filteredNoSweat.map(bet => ({ type: 'nosweat', key: bet.id, sortDate: parseUtc(bet.placedAt), data: bet })),
            ...filteredPairs.map(pair => ({ type: 'bag', key: pair.qualifyingBet.id, sortDate: parseUtc(pair.qualifyingBet.placedAt), data: pair })),
            ...filtered.map(bet => ({ type: 'regular', key: bet.id, sortDate: parseUtc(bet.placedAt), data: bet })),
          ]
            .sort((a, b) => (b.sortDate || 0) - (a.sortDate || 0))
            .map(item => {
              if (item.type === 'deposit') {
                const group = item.data;
                return (
                  <DepositBonusCard
                    key={item.key}
                    rolloverBets={group}
                    onSettle={handleSettle}
                    onDelete={(ids) => {
                      ids.forEach(id => {
                        fetch(`${API_BASE}/user/bets/${id}`, {
                          method: 'DELETE',
                          headers: { Authorization: `Bearer ${group[0]._token}` },
                        });
                      });
                      setBets(prev => prev.filter(b => !ids.includes(b.id)));
                    }}
                  />
                );
              }
              if (item.type === 'nosweat') return (
                <NoSweatCard key={item.key} bet={item.data} onSettle={handleSettle} onDelete={handleDelete} onEdit={handleEdit} onNavigate={onNavigate} />
              );
              if (item.type === 'bag') return (
                <BetAndGetCard key={item.key} qualifyingBet={item.data.qualifyingBet} bonusBet={item.data.bonusBet} onNavigate={onNavigate} onSettle={handleSettle} onDelete={handleDelete} onEdit={handleEdit} />
              );
              return (
                <BetCard key={item.key} bet={item.data} onSettle={handleSettle} onDelete={handleDelete} onEdit={handleEdit} />
              );
            })}
        </div>
      )}
    </div>
  );
}
